import type { Zone } from '../../services/zonesApi';

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function normalizePoint(value: unknown): [number, number] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const x = Number(value[0]);
  const y = Number(value[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return [clamp01(x), clamp01(y)];
}

export function zonePolygonFromBBoxNorm(bbox: unknown): number[][] | null {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const x = clamp01(Number(bbox[0]));
  const y = clamp01(Number(bbox[1]));
  const third = Number(bbox[2]);
  const fourth = Number(bbox[3]);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(third) || !Number.isFinite(fourth)) {
    return null;
  }

  const looksLikeXyxy = third > x && fourth > y && third <= 1.0001 && fourth <= 1.0001;
  const looksLikeXywh = third > 0 && fourth > 0 && (x + third) <= 1.0001 && (y + fourth) <= 1.0001;

  let x2 = third;
  let y2 = fourth;
  if (looksLikeXywh) {
    x2 = x + third;
    y2 = y + fourth;
  } else if (!looksLikeXyxy) {
    x2 = Math.max(x, third);
    y2 = Math.max(y, fourth);
  }

  const right = clamp01(x2);
  const bottom = clamp01(y2);
  if (right <= x || bottom <= y) return null;

  return [
    [x, y],
    [right, y],
    [right, bottom],
    [x, bottom],
  ];
}

export function zoneOverlayPolygon(zone: Partial<Zone> | null | undefined): number[][] | null {
  if (!zone) return null;
  const polygon = Array.isArray(zone.polygon_norm)
    ? zone.polygon_norm.map((point) => normalizePoint(point)).filter(Boolean) as [number, number][]
    : [];
  if (polygon.length >= 3) {
    return polygon.map(([x, y]) => [x, y]);
  }
  return zonePolygonFromBBoxNorm(zone.bbox_norm);
}

export function zoneOverlayLabel(zone: Partial<Zone> | null | undefined): string {
  return String(zone?.name || zone?.zone_id || '').trim();
}

export function zoneOverlayAnchor(zone: Partial<Zone> | null | undefined): [number, number] | null {
  const polygon = zoneOverlayPolygon(zone);
  if (!polygon || polygon.length === 0) return null;

  let minX = 1;
  let minY = 1;
  for (const point of polygon) {
    const x = clamp01(Number(point?.[0] ?? 0));
    const y = clamp01(Number(point?.[1] ?? 0));
    if (x < minX) minX = x;
    if (y < minY) minY = y;
  }

  return [clamp01(minX + 0.012), clamp01(minY + 0.024)];
}
